import { Component, Input } from '@angular/core';
import type {
  ThemeSentimentBreakdown,
  SubThemeSentimentBreakdown,
} from '../../../../services/sentiment-analysis-stats/sentiment-analysis-stats.service';

export interface ThemeWithSubThemes {
  themeId: string;
  themeName: string;
  count: number;
  percentOfSentiment: number;
  subThemes: Array<{ subThemeName: string; count: number }>;
}

@Component({
  selector: 'app-sentiment-details-top-themes',
  standalone: false,
  templateUrl: './details-top-themes.component.html',
  styleUrls: ['./details-top-themes.component.scss'],
})
export class SentimentDetailsTopThemesComponent {
  @Input() themeBreakdown: ThemeSentimentBreakdown[] = [];
  @Input() subThemeBreakdown: SubThemeSentimentBreakdown[] = [];
  @Input() themeLimit = 3;
  @Input() subThemeLimitPerTheme = 2;
  @Input() showPositive = true;
  @Input() showNegative = true;

  get totalPositive(): number {
    return (this.themeBreakdown || []).reduce(
      (sum, t) => sum + (t.sentiment?.positive?.count ?? 0),
      0
    );
  }

  get totalNegative(): number {
    return (this.themeBreakdown || []).reduce(
      (sum, t) => sum + (t.sentiment?.negative?.count ?? 0),
      0
    );
  }

  get positiveThemesWithSubThemes(): ThemeWithSubThemes[] {
    const themes = (this.themeBreakdown || [])
      .map((t) => ({
        themeId: t.themeId,
        themeName: t.themeName,
        count: t.sentiment?.positive?.count ?? 0,
      }))
      .filter((t) => t.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, this.themeLimit);
    const total = this.totalPositive || 1;
    const subByTheme = this.getSubThemesByTheme('positive');
    return themes.map((t) => ({
      ...t,
      percentOfSentiment: Math.round((t.count / total) * 100),
      subThemes: (subByTheme.get(t.themeId) || []).slice(0, this.subThemeLimitPerTheme),
    }));
  }

  get negativeThemesWithSubThemes(): ThemeWithSubThemes[] {
    const themes = (this.themeBreakdown || [])
      .map((t) => ({
        themeId: t.themeId,
        themeName: t.themeName,
        count: t.sentiment?.negative?.count ?? 0,
      }))
      .filter((t) => t.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, this.themeLimit);
    const total = this.totalNegative || 1;
    const subByTheme = this.getSubThemesByTheme('negative');
    return themes.map((t) => ({
      ...t,
      percentOfSentiment: Math.round((t.count / total) * 100),
      subThemes: (subByTheme.get(t.themeId) || []).slice(0, this.subThemeLimitPerTheme),
    }));
  }

  private getSubThemesByTheme(
    sentiment: 'positive' | 'negative'
  ): Map<string, Array<{ subThemeName: string; count: number }>> {
    const map = new Map<string, Array<{ subThemeName: string; count: number }>>();
    (this.subThemeBreakdown || []).forEach((s) => {
      const count = s.sentiment?.[sentiment]?.count ?? 0;
      if (count <= 0) return;
      const list = map.get(s.themeId) || [];
      list.push({ subThemeName: s.subThemeName, count });
      map.set(s.themeId, list);
    });
    map.forEach((list) => list.sort((a, b) => b.count - a.count));
    return map;
  }
}
